import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { clientApi, ScenarioDossier, ScenarioDossierUpdate } from '@/api/client'
import { qk } from '@/api/queryKeys'
import Topbar from '@/components/Topbar'
import BackLink from '@/components/BackLink'
import ErrorBox from '@/components/ErrorBox'
import Skeleton from '@/components/Skeleton'
import { timeAgo } from '@/utils/format'

function statusPill(status?: string) {
  const s = String(status || '').toLowerCase()
  if (s === 'realized') return 'pill ok'
  if (s === 'disproven') return 'pill bad'
  return 'pill'
}

export default function ScenarioDetail() {
  const { scenarioId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [statusError, setStatusError] = useState<string | null>(null)
  const [actionMsg, setActionMsg] = useState<string | null>(null)

  const detailQuery = useQuery({
    queryKey: qk.scenarios.detail(scenarioId || ''),
    queryFn: () => clientApi.getScenario(scenarioId as string),
    enabled: !!scenarioId,
    staleTime: 10_000,
  })

  const dossierQuery = useQuery({
    queryKey: qk.scenarios.dossier(scenarioId || ''),
    queryFn: () => clientApi.getScenarioDossier(scenarioId as string),
    enabled: !!scenarioId,
    staleTime: 10_000,
  })

  const refreshMut = useMutation({
    mutationFn: () => clientApi.refreshScenarioDossier(scenarioId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.scenarios.dossier(scenarioId as string) })
      setActionMsg('Scenario dossier refreshed.')
    },
    onError: (e: any) => setStatusError(e?.message || 'Dossier refresh failed'),
  })

  const reforecastMut = useMutation({
    mutationFn: () => clientApi.reforecastScenario(scenarioId as string),
    onSuccess: (res) => {
      setActionMsg(`Re-forecast queued (job ${res.job_id}).`)
    },
    onError: (e: any) => setStatusError(e?.message || 'Re-forecast failed'),
  })

  const archiveMut = useMutation({
    mutationFn: () => clientApi.archiveScenario(scenarioId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.scenarios.all() })
      navigate('/universe/scenarios')
    },
    onError: (e: any) => setStatusError(e?.message || 'Archive failed'),
  })

  const statusMut = useMutation({
    mutationFn: (status: 'hypothetical' | 'active' | 'realized' | 'disproven') =>
      clientApi.updateScenario(scenarioId as string, { status }),
    onSuccess: (_res, status) => {
      queryClient.invalidateQueries({ queryKey: qk.scenarios.detail(scenarioId as string) })
      queryClient.invalidateQueries({ queryKey: qk.scenarios.dossier(scenarioId as string) })
      queryClient.invalidateQueries({ queryKey: qk.scenarios.all() })
      setStatusError(null)
      const label: Record<typeof status, string> = {
        hypothetical: 'Marked hypothetical.',
        active: 'Scenario reopened — now active.',
        realized: 'Marked realized. Attached forecasts now treat the assumptions as confirmed.',
        disproven: 'Marked disproven. Attached forecasts are tagged as stale.',
      }
      setActionMsg(label[status])
    },
    onError: (e: any) => setStatusError(e?.message || 'Status change failed'),
  })

  const data = detailQuery.data
  const scenario = data?.scenario
  const questions = data?.questions || []

  if (!scenarioId) {
    return (
      <div className="container">
        <Topbar subtitle="Scenario" />
        <BackLink defaultTo="/universe/scenarios" defaultLabel="Scenarios" />
        <ErrorBox error={new Error('No scenario id in URL')} />
      </div>
    )
  }

  return (
    <div className="container">
      <Topbar subtitle="Scenario detail" />
      <BackLink defaultTo="/universe/scenarios" defaultLabel="Scenarios" />

      {detailQuery.isLoading && (
        <div className="grid" style={{ marginTop: 16 }}>
          <Skeleton variant="card" count={2} />
        </div>
      )}

      {detailQuery.error && <ErrorBox error={detailQuery.error} />}

      {!detailQuery.isLoading && scenario && (
        <div className="grid">
          <div className="card">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0, overflowWrap: 'break-word' }}>{scenario.label}</h2>
                {scenario.description && (
                  <div className="muted" style={{ marginTop: 6, lineHeight: 1.55 }}>{scenario.description}</div>
                )}
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <span className={statusPill(scenario.status)}>{scenario.status || 'hypothetical'}</span>
                  <span className="pill">{scenario.visibility}</span>
                  {scenario.updated_at && <span className="pill">updated {timeAgo(scenario.updated_at)}</span>}
                  {scenario.created_at && <span className="pill">created {timeAgo(scenario.created_at)}</span>}
                  {scenario.resolved_at && <span className="pill">resolved {timeAgo(scenario.resolved_at)}</span>}
                </div>
              </div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn"
                  disabled={refreshMut.isPending}
                  onClick={() => refreshMut.mutate()}
                  title="Rebuild the scenario dossier from current assumptions"
                >
                  {refreshMut.isPending ? 'Refreshing…' : 'Refresh dossier'}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={reforecastMut.isPending}
                  onClick={() => {
                    if (confirm('Queue a conditional re-forecast for every question attached to this scenario? This respects your org rate limits.')) {
                      reforecastMut.mutate()
                    }
                  }}
                  title="Re-run every attached question under these assumptions"
                >
                  {reforecastMut.isPending ? 'Queuing…' : 'Re-forecast all'}
                </button>
                {scenario.status === 'realized' || scenario.status === 'disproven' ? (
                  <button
                    type="button"
                    className="btn"
                    disabled={statusMut.isPending}
                    onClick={() => statusMut.mutate('active')}
                    title="Return the scenario to active so it can be forecast against again"
                  >
                    Reopen
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn"
                      disabled={statusMut.isPending}
                      onClick={() => statusMut.mutate('realized')}
                      title="The assumptions in this scenario have come true"
                    >
                      Mark realized
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={statusMut.isPending}
                      onClick={() => statusMut.mutate('disproven')}
                      title="The assumptions in this scenario are no longer possible"
                    >
                      Mark disproven
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="btn"
                  disabled={archiveMut.isPending || scenario.status === 'archived'}
                  onClick={() => {
                    if (confirm('Archive this scenario? It will be hidden from the default list but its history is preserved.')) {
                      archiveMut.mutate()
                    }
                  }}
                >
                  Archive
                </button>
              </div>
            </div>

            {statusError && (
              <div className="muted" style={{ fontSize: 12, color: 'var(--bad)', marginTop: 8 }}>
                {statusError}
              </div>
            )}
            {actionMsg && (
              <div className="muted" style={{ fontSize: 12, color: 'var(--ok)', marginTop: 8 }}>
                {actionMsg}
              </div>
            )}
          </div>

          <ScenarioDossierCard
            dossier={dossierQuery.data?.dossier}
            updates={dossierQuery.data?.updates || []}
            isLoading={dossierQuery.isLoading}
          />

          <AssumptionsCard
            scenarioId={scenario.scenario_id}
            label={scenario.label}
            description={scenario.description || null}
            assumptions={scenario.assumptions || []}
            tags={scenario.tags || null}
            onAfterSave={() => {
              queryClient.invalidateQueries({ queryKey: qk.scenarios.detail(scenario.scenario_id) })
              queryClient.invalidateQueries({ queryKey: qk.scenarios.dossier(scenario.scenario_id) })
              queryClient.invalidateQueries({ queryKey: qk.scenarios.all() })
            }}
            setActionMsg={setActionMsg}
            setStatusError={setStatusError}
          />

          <div className="card">
            <h2>Linked questions</h2>
            <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
              Questions that have been forecasted conditional on this scenario.
            </div>
            {questions.length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>
                No questions attached yet. Attach this scenario when asking a question to start building a history.
              </div>
            ) : (
              <div className="stack" style={{ gap: 0 }}>
                {questions.map((q) => (
                  <div key={q.question_id} className="signal-row">
                    <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600, lineHeight: 1.45 }}>
                          {q.title || `Question #${q.question_id}`}
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                          {q.status ? `Status: ${q.status}` : ''}
                          {q.attached_at ? ` · Attached ${timeAgo(q.attached_at)}` : ''}
                          {q.latest_conditional_run_id ? ` · Last run #${q.latest_conditional_run_id}` : ''}
                        </div>
                      </div>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {q.item_id && (
                          <Link className="btn small" to={`/items/${q.item_id}`}>
                            View forecast
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function AssumptionsCard({
  scenarioId,
  label,
  description,
  assumptions,
  tags,
  onAfterSave,
  setActionMsg,
  setStatusError,
}: {
  scenarioId: string
  label: string
  description: string | null
  assumptions: { id?: string | null; text: string }[]
  tags: string[] | null
  onAfterSave: () => void
  setActionMsg: (s: string | null) => void
  setStatusError: (s: string | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draftLabel, setDraftLabel] = useState(label)
  const [draftDesc, setDraftDesc] = useState(description || '')
  const [draftAssump, setDraftAssump] = useState<{ id?: string | null; text: string }[]>(assumptions)

  const saveMut = useMutation({
    mutationFn: () =>
      clientApi.updateScenario(scenarioId, {
        label: draftLabel.trim(),
        description: draftDesc.trim() || null,
        assumptions: draftAssump.filter((a) => a.text.trim()).map((a) => ({ id: a.id, text: a.text.trim() })),
      }),
    onSuccess: (res) => {
      setEditing(false)
      const msgBits: string[] = []
      if (res.queued_refresh_job_id) msgBits.push('dossier refresh queued')
      if (res.queued_reforecast_job_id) msgBits.push('conditional re-forecast queued')
      setActionMsg(msgBits.length ? `Saved — ${msgBits.join(' · ')}.` : 'Saved.')
      setStatusError(null)
      onAfterSave()
    },
    onError: (e: any) => setStatusError(e?.message || 'Save failed'),
  })

  function updateDraftAt(idx: number, text: string) {
    setDraftAssump((prev) => prev.map((a, i) => (i === idx ? { ...a, text } : a)))
  }
  function removeDraftAt(idx: number) {
    setDraftAssump((prev) => prev.filter((_, i) => i !== idx))
  }
  function addDraft() {
    setDraftAssump((prev) => [...prev, { id: null, text: '' }])
  }
  function cancelEdit() {
    setDraftLabel(label)
    setDraftDesc(description || '')
    setDraftAssump(assumptions)
    setEditing(false)
  }

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Assumptions</h2>
        {!editing && (
          <button type="button" className="btn small" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>

      {!editing && (
        <>
          {assumptions.length > 0 ? (
            <ul style={{ margin: '8px 0 0 18px', padding: 0, lineHeight: 1.6 }}>
              {assumptions.map((a, i) => (
                <li key={a.id || i}>{a.text}</li>
              ))}
            </ul>
          ) : (
            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>No assumptions recorded.</div>
          )}
          {tags && tags.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              {tags.map((t) => (
                <span key={t} className="pill">{t}</span>
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <div className="stack" style={{ gap: 10, marginTop: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Label
            <input
              type="text"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              style={{ display: 'block', width: '100%', padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', marginTop: 4 }}
            />
          </label>
          <label style={{ fontSize: 12, fontWeight: 700 }}>
            Description
            <textarea
              value={draftDesc}
              onChange={(e) => setDraftDesc(e.target.value)}
              rows={3}
              style={{ display: 'block', width: '100%', padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', marginTop: 4 }}
            />
          </label>
          <div>
            <div className="sectionTitle" style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)' }}>
              Assumptions (treated as true)
            </div>
            <div className="stack" style={{ gap: 6, marginTop: 6 }}>
              {draftAssump.map((a, i) => (
                <div key={i} className="row" style={{ gap: 6, alignItems: 'flex-start' }}>
                  <input
                    type="text"
                    value={a.text}
                    onChange={(e) => updateDraftAt(i, e.target.value)}
                    placeholder="e.g. US sends ground troops to Iran in 2026"
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)' }}
                  />
                  <button type="button" className="btn small" onClick={() => removeDraftAt(i)} title="Remove assumption">
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="btn small" onClick={addDraft}>
                Add assumption
              </button>
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
              Editing assumptions invalidates prior conditional forecasts — we'll queue a re-forecast of every attached question.
            </div>
          </div>

          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="btn primary"
              disabled={saveMut.isPending || !draftLabel.trim()}
              onClick={() => saveMut.mutate()}
            >
              {saveMut.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="btn" onClick={cancelEdit} disabled={saveMut.isPending}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Bullets({ items, emptyMessage }: { items: string[] | null | undefined; emptyMessage?: string }) {
  const rows = Array.isArray(items) ? items.filter((s) => typeof s === 'string' && s.trim()) : []
  if (rows.length === 0) {
    return emptyMessage ? (
      <div className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>{emptyMessage}</div>
    ) : null
  }
  return (
    <ul style={{ margin: '4px 0 0 18px', padding: 0, lineHeight: 1.55, fontSize: 13 }}>
      {rows.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  )
}

function ScenarioDossierCard({
  dossier,
  updates,
  isLoading,
}: {
  dossier?: ScenarioDossier | null
  updates: ScenarioDossierUpdate[]
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="card">
        <h2>Scenario dossier</h2>
        <div className="muted" style={{ fontSize: 12 }}>Loading…</div>
      </div>
    )
  }

  const hasDossier = !!(
    dossier &&
    (dossier.prompt_safe_summary ||
      (dossier.prompt_safe_key_points && dossier.prompt_safe_key_points.length > 0) ||
      (dossier.prompt_safe_drivers && dossier.prompt_safe_drivers.length > 0))
  )

  return (
    <div className="card">
      <h2>Scenario dossier</h2>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        This brief is injected into every conditional forecast run that uses this scenario, so agents share
        a consistent view of what the assumptions entail.
      </div>

      {!hasDossier && (
        <div className="muted" style={{ fontSize: 13 }}>
          No dossier yet. Use <em>Refresh dossier</em> above to generate one. (The worker also builds it automatically
          on scenario create and after each conditional forecast.)
        </div>
      )}

      {hasDossier && dossier && (
        <div className="stack" style={{ gap: 14 }}>
          {dossier.prompt_safe_summary && (
            <div>
              <div className="sectionTitle" style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)' }}>
                Summary
              </div>
              <div style={{ marginTop: 4, lineHeight: 1.55 }}>{dossier.prompt_safe_summary}</div>
            </div>
          )}

          <div>
            <div className="sectionTitle" style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)' }}>
              Key assumption entailments
            </div>
            <Bullets items={dossier.prompt_safe_key_points} emptyMessage="None recorded yet." />
          </div>

          <div>
            <div className="sectionTitle" style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)' }}>
              Drivers (push more extreme)
            </div>
            <Bullets items={dossier.prompt_safe_drivers} emptyMessage="None recorded yet." />
          </div>

          <div>
            <div className="sectionTitle" style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)' }}>
              Inhibitors (push less extreme)
            </div>
            <Bullets items={dossier.prompt_safe_inhibitors} emptyMessage="None recorded yet." />
          </div>

          {dossier.updated_at && (
            <div className="muted" style={{ fontSize: 11 }}>
              Last refreshed {timeAgo(dossier.updated_at)}
              {dossier.last_refresh_run_id ? ` · triggered by run #${dossier.last_refresh_run_id}` : ''}
            </div>
          )}
        </div>
      )}

      {updates.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="sectionTitle" style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: 'var(--muted)' }}>
            Recent refreshes
          </div>
          <div className="stack" style={{ gap: 0, marginTop: 6 }}>
            {updates.slice(0, 5).map((u) => (
              <div key={u.id} className="signal-row" style={{ paddingTop: 8, paddingBottom: 8 }}>
                <div className="row" style={{ justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
                  <span className="pill">{u.update_type}</span>
                  <span className="muted">
                    {u.as_of ? timeAgo(u.as_of) : 'unknown'}
                    {u.run_id ? ` · run #${u.run_id}` : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
